// Agrupa filas de cost_allocations (una por animal-allocation) por sourceEventId
// para reconstruir la "lista de costos" que el usuario ve. La API devuelve
// flat por-animal porque el backend almacena la proyección derivada — agrupar
// es responsabilidad del cliente.
//
// Money math: el server ya redondea cada allocation a 2 decimales (centavos
// enteros internamente, ver D2). Sumamos en centavos enteros para evitar
// drift IEEE-754 al re-agregar.

import type { CostAllocationRow } from "../api/costAllocationsApi";
import type { CostCategory } from "./categories";

export type GroupedCost = {
  sourceEventId: string;
  lotId: string | null;
  category: CostCategory;
  periodStart: string;
  periodEnd: string;
  occurredAt: string;
  totalAmount: string; // canonical decimal "1234567.89"
  animalCount: number;
};

function toCents(amount: string): number {
  // "33.33" → 3333. Asumimos que el server siempre manda 2 decimales o entero.
  // Multiplicar por 100 con float y redondear es seguro para magnitudes
  // dentro de Number.MAX_SAFE_INTEGER / 100 (~90 trillones de céntimos = 900T₡).
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function centsToString(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const intPart = Math.floor(abs / 100);
  const decPart = abs % 100;
  return `${sign}${intPart}.${String(decPart).padStart(2, "0")}`;
}

export function groupBySourceEvent(rows: CostAllocationRow[]): GroupedCost[] {
  const groups = new Map<string, { g: GroupedCost; cents: number }>();

  for (const row of rows) {
    const existing = groups.get(row.sourceEventId);
    if (existing) {
      existing.cents += toCents(row.amount);
      existing.g.animalCount += 1;
    } else {
      groups.set(row.sourceEventId, {
        cents: toCents(row.amount),
        g: {
          sourceEventId: row.sourceEventId,
          lotId: row.lotId,
          category: row.category,
          periodStart: row.periodStart,
          periodEnd: row.periodEnd,
          occurredAt: row.occurredAt,
          totalAmount: "0",
          animalCount: 1,
        },
      });
    }
  }

  return Array.from(groups.values())
    .map(({ g, cents }) => ({ ...g, totalAmount: centsToString(cents) }))
    .sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
}
